/**
 * BYK-17: Style Transfer - AI Material Palette Mapping
 *
 * Component for selecting and applying style palettes to the kitchen design.
 */

import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import type { StylePalette, StylePresetName } from "@byk/shared";
import {
  getStylePresets,
  getPresetByName,
  getPaletteSwatches,
  createPalettePreviewGradient,
} from "@/services/stylePalette";

interface StylePaletteSelectorProps {
  onApply?: (palette: StylePalette) => void;
  className?: string;
}

/**
 * Color swatch component for palette preview
 */
const ColorSwatch: React.FC<{
  color: string;
  label: string;
  size?: "sm" | "md";
}> = ({ color, label, size = "md" }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={cn(
        "rounded-full border border-white/50 shadow-sm",
        size === "sm" ? "h-5 w-5" : "h-7 w-7"
      )}
      style={{ backgroundColor: color }}
      title={label}
    />
    <span className={cn("text-gray-500", size === "sm" ? "text-[9px]" : "text-[10px]")}>
      {label}
    </span>
  </div>
);

/**
 * Individual palette card component
 */
const PaletteCard: React.FC<{
  palette: StylePalette;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ palette, isSelected, onSelect }) => {
  const swatches = useMemo(() => getPaletteSwatches(palette), [palette]);
  const gradient = useMemo(() => createPalettePreviewGradient(palette), [palette]);

  return (
    <motion.button
      layout
      onClick={onSelect}
      className={cn(
        "w-full rounded-xl border p-3 text-left transition-all",
        "bg-white/60 hover:bg-white",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        isSelected
          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-500/30"
          : "border-gray-200 hover:border-gray-300"
      )}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      role="radio"
      aria-checked={isSelected}
    >
      {/* Preview gradient bar */}
      <div
        className="mb-2 h-3 w-full rounded-full"
        style={{ background: gradient }}
      />

      {/* Name and description */}
      <div className="mb-2">
        <h4 className="text-sm font-medium text-gray-900">{palette.name}</h4>
        {palette.description && (
          <p className="text-xs text-gray-500">{palette.description}</p>
        )}
      </div>

      {/* Color swatches */}
      <div className="flex items-center justify-between px-1">
        {swatches.map((swatch) => (
          <ColorSwatch
            key={swatch.label}
            color={swatch.color}
            label={swatch.label}
            size="sm"
          />
        ))}
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="mt-2 flex items-center justify-center gap-1 text-xs text-blue-600">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>Applied</span>
        </div>
      )}
    </motion.button>
  );
};

/**
 * Custom palette input component
 */
const CustomPaletteInput: React.FC<{
  onGenerate: (description: string) => void;
  isLoading?: boolean;
}> = ({ onGenerate, isLoading }) => {
  const [description, setDescription] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (description.trim()) {
      onGenerate(description.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <label className="block text-xs font-medium text-gray-700">
        Custom AI Palette
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., warm earthy tones with brass..."
          className={cn(
            "flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm",
            "placeholder-gray-400",
            "focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          )}
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={!description.trim() || isLoading}
          className={cn(
            "rounded-lg px-3 py-2 text-sm font-medium",
            "bg-gradient-to-r from-purple-500 to-blue-500 text-white",
            "hover:from-purple-600 hover:to-blue-600",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "transition-all"
          )}
        >
          {isLoading ? (
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
          )}
        </button>
      </div>
      <p className="text-[10px] text-gray-400">
        Describe your desired style and AI will generate matching colors
      </p>
    </form>
  );
};

/**
 * Main StylePaletteSelector component
 */
export const StylePaletteSelector: React.FC<StylePaletteSelectorProps> = ({
  onApply,
  className,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<StylePresetName>("warm-2025");
  const [isGenerating, setIsGenerating] = useState(false);
  const [customPalettes, setCustomPalettes] = useState<StylePalette[]>([]);

  const presets = useMemo(() => getStylePresets(), []);

  const handleSelectPreset = useCallback(
    (presetName: StylePresetName) => {
      setSelectedPreset(presetName);
      const palette = getPresetByName(presetName);
      if (palette && onApply) {
        onApply(palette);
      }
    },
    [onApply]
  );

  const handleCustomPalette = useCallback(
    async (description: string) => {
      setIsGenerating(true);

      // TODO: Integrate with AI API for custom palette generation
      // For now, we'll create a variation of the selected preset
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const basePalette = getPresetByName(selectedPreset);
      if (basePalette) {
        const customPalette: StylePalette = {
          ...basePalette,
          id: `custom-${Date.now()}`,
          name: `Custom: ${description.slice(0, 20)}...`,
          description: description,
          isPreset: false,
        };
        setCustomPalettes((prev) => [...prev, customPalette]);
        if (onApply) {
          onApply(customPalette);
        }
      }

      setIsGenerating(false);
    },
    [selectedPreset, onApply]
  );

  const allPalettes = useMemo(
    () => [...presets, ...customPalettes],
    [presets, customPalettes]
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-purple-100 to-blue-100">
          <svg
            className="h-4 w-4 text-purple-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Style Palette</h3>
          <p className="text-xs text-gray-500">Apply a cohesive color scheme</p>
        </div>
      </div>

      {/* Custom AI input */}
      <div className="mb-4 rounded-xl bg-gradient-to-r from-purple-50 to-blue-50 p-3">
        <CustomPaletteInput
          onGenerate={handleCustomPalette}
          isLoading={isGenerating}
        />
      </div>

      {/* Presets grid */}
      <div className="space-y-3" role="radiogroup" aria-label="Style palettes">
        <h4 className="text-xs font-medium uppercase tracking-wider text-gray-400">
          Presets
        </h4>
        <AnimatePresence mode="popLayout">
          <div className="grid grid-cols-1 gap-2">
            {allPalettes.map((palette) => (
              <PaletteCard
                key={palette.id}
                palette={palette}
                isSelected={
                  palette.isPreset
                    ? selectedPreset === palette.presetName
                    : false
                }
                onSelect={() => {
                  if (palette.isPreset && palette.presetName) {
                    handleSelectPreset(palette.presetName);
                  } else if (onApply) {
                    onApply(palette);
                  }
                }}
              />
            ))}
          </div>
        </AnimatePresence>
      </div>

      {/* Currently selected indicator */}
      {selectedPreset && (
        <div className="mt-4 rounded-lg bg-blue-50 p-2 text-center">
          <span className="text-xs text-blue-600">
            Active: <strong>{getPresetByName(selectedPreset)?.name}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

export default StylePaletteSelector;
